<script lang="ts">
  /**
   * The dialog shell, ported from the site's `shared/ui/Modal`.
   *
   * Taken: the 20px surface radius, the panel background, the shadow, the 90vh ceiling, and a round
   * 40px close button that tints on hover instead of being a bare ×.
   *
   * Left behind: a fixed `width: 560px; min-height: 730px`, which would make a two-field dialog as
   * tall as a page, and a `z-index: 9999 !important` fighting for the top of the stack. Width is a
   * prop here because our four dialogs genuinely differ — a family's detail is not a member list —
   * and the point is that they now differ by one declared step instead of by four ad-hoc max-widths.
   *
   * Escape closes, the overlay closes, and focus moves into the dialog on open. None of that was in
   * the original; all of it is what a dialog owes a keyboard. Some flows (long publish forms) can
   * opt out of accidental dismissal.
   */
  import { onMount } from "svelte";
  import type { Snippet } from "svelte";

  let {
    title = "",
    subtitle = "",
    /** sm for a form, md for a detail view, lg for a queue with previews. */
    size = "md",
    closeLabel = "Close",
    closeOnOverlay = true,
    closeOnEscape = true,
    /** Pins close + actions fixed in the panel's top-right corner, on top of everything, while
     *  media, header and body scroll together as one unit beneath them. For a dialog whose media
     *  is tall (a 3D preview) and whose body can run long (a parameter list): reserving permanent
     *  space for media + header above a separately-scrolling body starves the body of height, and
     *  nesting another scrollbar inside it just moves the problem instead of fixing it. */
    floatActions = false,
    onclose,
    media,
    header,
    actions,
    children,
  }: {
    title?: string;
    subtitle?: string;
    size?: "sm" | "md" | "lg";
    closeLabel?: string;
    closeOnOverlay?: boolean;
    closeOnEscape?: boolean;
    floatActions?: boolean;
    onclose?: () => void;
    media?: Snippet;
    header?: Snippet;
    actions?: Snippet;
    children?: Snippet;
  } = $props();

  let panel = $state<HTMLElement | null>(null);

  const SIZES = { sm: "max-w-lg", md: "max-w-2xl", lg: "max-w-4xl" } as const;

  onMount(() => {
    function onKeydown(event: KeyboardEvent) {
      if (closeOnEscape && event.key === "Escape") onclose?.();
    }
    document.addEventListener("keydown", onKeydown);
    // Focus the panel rather than the first control: landing on the close button means a stray Enter
    // dismisses what the user just opened. bind:this has already run by the time onMount fires, so
    // panel is guaranteed non-null here.
    panel?.focus();
    return () => document.removeEventListener("keydown", onKeydown);
  });

  function onOverlayClick(event: MouseEvent) {
    if (closeOnOverlay && event.target === event.currentTarget) onclose?.();
  }
</script>

<!-- Decorative click-to-close backdrop; the actual interactive surface is the dialog panel below,
     and Escape (handled in onMount) already covers keyboard-only dismissal. -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="fixed inset-0 z-50 flex items-center justify-center p-4"
  style="background: var(--brand-overlay)"
  onclick={onOverlayClick}
  role="presentation"
>
  <div
    bind:this={panel}
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    class="w-full max-h-[90vh] overflow-hidden rounded-surface bg-page shadow-surface outline-none flex flex-col {SIZES[
      size
    ]} {floatActions ? 'relative' : ''}"
  >
    {#if floatActions}
      <!-- Fixed to the panel itself, not to the scrolling content beneath it — stays reachable no
           matter how far media and body have scrolled past underneath. -->
      <div class="absolute top-2 right-2 z-10 flex items-center gap-1">
        {@render actions?.()}
        <button
          class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white/90
                 backdrop-blur-sm text-ink-secondary transition-colors duration-200 ease-out
                 hover:bg-white"
          aria-label={closeLabel}
          title={closeLabel}
          onclick={() => onclose?.()}
        >
          <svg
            viewBox="0 0 24 24"
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto flex flex-col">
        <!-- Edge-to-edge, above everything: a family's 3D preview is the first thing the dialog is
             for, and insetting it would make it look like an attachment. -->
        {#if media}
          <div class="shrink-0">{@render media()}</div>
        {/if}

        {#if title || header}
          <div class="shrink-0 p-5 pb-0">
            {#if header}
              {@render header()}
            {:else}
              <h2 class="text-lg font-semibold truncate">{title}</h2>
              {#if subtitle}
                <p class="text-xs text-ink-muted mt-0.5">{subtitle}</p>
              {/if}
            {/if}
          </div>
        {/if}

        <div class="p-5 flex flex-col gap-4">
          {@render children?.()}
        </div>
      </div>
    {:else}
      <!-- Edge-to-edge, above everything and outside the padding: a family's 3D preview is the
           first thing the dialog is for, and insetting it would make it look like an attachment. -->
      {#if media}
        <div class="shrink-0">
          {@render media()}
        </div>
      {/if}

      {#if title || header}
        <div class="shrink-0 flex items-start gap-3 p-5 pb-0">
          <div class="flex-1 min-w-0">
            {#if header}
              {@render header()}
            {:else}
              <h2 class="text-lg font-semibold truncate">{title}</h2>
              {#if subtitle}
                <p class="text-xs text-ink-muted mt-0.5">{subtitle}</p>
              {/if}
            {/if}
          </div>

          {@render actions?.()}

          <!-- A tint on hover rather than a colour change, which is how the site's close button
               behaves: the × is already the quietest thing on screen and does not need to shout. -->
          <button
            class="shrink-0 w-10 h-10 -mt-1 -mr-1 rounded-full flex items-center justify-center
                   text-ink-secondary transition-colors duration-200 ease-out
                   hover:bg-[var(--brand-press-hover)]"
            aria-label={closeLabel}
            title={closeLabel}
            onclick={() => onclose?.()}
          >
            <svg
              viewBox="0 0 24 24"
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      {/if}

      <div class="p-5 flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
        {@render children?.()}
      </div>
    {/if}
  </div>
</div>
